const SQRT_PI = Math.sqrt(Math.PI);
export const MARKET_SQUARE_GAP = 1;
const cache = new WeakMap();

function seed(key) {
  let value = 2166136261;
  for (const character of String(key)) value = Math.imul(value ^ character.charCodeAt(0), 16777619);
  return (value >>> 0) / 4294967296;
}

// The enclosure keeps the scale of a square sized for packWidth, but may be
// narrower than that square when the rendered panel is narrower.
export function squareEnclosure(width, height, packWidth = width) {
  const side = Math.max(1, Math.min(packWidth - 24, height - 72));
  const w = Math.max(1, Math.min(side, width - 24));
  return { x: (width - w) / 2, y: 40, side, w, h: side };
}

function clamp(node, bounds) {
  const half = node.side / 2;
  node.x = Math.max(bounds.x + half, Math.min(bounds.x + bounds.w - half, node.x));
  node.y = Math.max(bounds.y + half, Math.min(bounds.y + bounds.h - half, node.y));
}

function collisionIndex(gap) {
  const levels = new Map();
  const cellKey = (x, y) => x * 1048576 + y;
  return {
    add(node) {
      const size = 2 ** Math.ceil(Math.log2(Math.max(1, node.side + gap)));
      if (!levels.has(size)) levels.set(size, new Map());
      const cells = levels.get(size);
      const key = cellKey(Math.floor(node.x / size), Math.floor(node.y / size));
      if (!cells.has(key)) cells.set(key, []);
      cells.get(key).push(node);
    },
    hit(node) {
      for (const [size, cells] of levels) {
        const reach = Math.ceil(((node.side + gap) / 2 + size / 2) / size);
        const x = Math.floor(node.x / size), y = Math.floor(node.y / size);
        for (let ix = x - reach; ix <= x + reach; ix++) {
          for (let iy = y - reach; iy <= y + reach; iy++) {
            const neighbors = cells.get(cellKey(ix, iy));
            if (!neighbors) continue;
            for (const other of neighbors) {
              const distance = (node.side + other.side) / 2 + gap;
              if (Math.abs(node.x - other.x) < distance - .00001 &&
                Math.abs(node.y - other.y) < distance - .00001) return other;
            }
          }
        }
      }
      return null;
    },
  };
}

function place(node, index, bounds, gap) {
  clamp(node, bounds);
  const queue = [{ x: node.x, y: node.y, cost: 0 }];
  const visited = new Set();
  for (let attempt = 0; queue.length && attempt < 256; attempt++) {
    queue.sort((a, b) => b.cost - a.cost);
    const candidate = queue.pop();
    node.x = candidate.x;
    node.y = candidate.y;
    const other = index.hit(node);
    if (!other) return;
    const distance = (node.side + other.side) / 2 + gap + .001;
    for (const [x, y] of [[other.x - distance, node.y], [other.x + distance, node.y],
      [node.x, other.y - distance], [node.x, other.y + distance]]) {
      const next = { x, y, side: node.side };
      clamp(next, bounds);
      const key = `${next.x.toFixed(3)}:${next.y.toFixed(3)}`;
      if (visited.has(key)) continue;
      visited.add(key);
      queue.push({ x: next.x, y: next.y, cost: (next.x - node.targetX) ** 2 + (next.y - node.targetY) ** 2 });
    }
  }
  // Low-discrepancy fallback uses free space without introducing rows or a grid.
  for (let attempt = 0; attempt < 20000; attempt++) {
    node.x = bounds.x + node.side / 2 + ((node.jitter + attempt * .754877666) % 1) * (bounds.w - node.side);
    node.y = bounds.y + node.side / 2 + ((node.jitter + attempt * .569840291) % 1) * (bounds.h - node.side);
    if (!index.hit(node)) return;
  }
  throw new Error("Market Mind squares cannot fit inside the current enclosure.");
}

export function settleMarketSquares(circleNodes, width, height, gap = MARKET_SQUARE_GAP,
  { packWidth = width, sideScale = SQRT_PI } = {}) {
  if (!circleNodes.length) return [];
  const key = `${width}:${height}:${gap}:${packWidth}:${sideScale}`;
  const entries = cache.get(circleNodes);
  if (entries?.has(key)) return entries.get(key);
  const bounds = squareEnclosure(width, height, packWidth);
  if (sideScale === 2) {
    const largestDiameter = circleNodes.reduce((largest, node) => Math.max(largest, node.r * 2), 0);
    bounds.w = Math.max(bounds.w, Math.min(width - 24, largestDiameter));
    bounds.x = (width - bounds.w) / 2;
    bounds.h = Math.max(bounds.h, Math.min(height - 60, largestDiameter));
  }
  const centerX = packWidth / 2, centerY = (height - 60) / 2;
  const radius = Math.min(packWidth, height - 60) / 2;
  const nodes = circleNodes.map((node, index) => {
    const side = node.r * sideScale;
    if (side > Math.min(bounds.w, bounds.h)) throw new Error("Market Mind square exceeds its enclosure.");
    const dx = (node.x - centerX) / radius, dy = (node.y - centerY) / radius;
    const norm = Math.max(Math.abs(dx), Math.abs(dy));
    const spread = norm ? Math.hypot(dx, dy) / norm : 1;
    const jitter = seed(node.data.id ?? node.data.name ?? index);
    const x = bounds.x + bounds.w / 2 + dx * spread * bounds.w / 2;
    const y = bounds.y + bounds.h / 2 + dy * spread * bounds.h / 2;
    return { ...node, side, x, y, targetX: x, targetY: y, jitter, index };
  });
  const ordered = [...nodes].sort((a, b) => b.side - a.side || a.jitter - b.jitter);
  for (let tick = 0; tick < 3; tick++) {
    const index = collisionIndex(gap);
    for (const node of ordered) {
      node.x += (node.targetX - node.x) * .05;
      node.y += (node.targetY - node.y) * .05;
      place(node, index, bounds, gap);
      index.add(node);
    }
  }
  const result = nodes.map(({ targetX, targetY, jitter, index, ...node }) => node);
  const saved = entries || new Map();
  if (saved.size >= 4) saved.delete(saved.keys().next().value);
  saved.set(key, result);
  cache.set(circleNodes, saved);
  return result;
}

export const MARKET_LABEL_OVERFLOW = 2;
const CHAR_WIDTH = .54;

// Fits a label into a mark, allowing it to spread modestly past the mark edge.
export function fitMarketLabel(name, markWidth, fontSize, { overflow = MARKET_LABEL_OVERFLOW, minCharacters = 0 } = {}) {
  const characters = Math.max(2, minCharacters, Math.floor((markWidth * overflow) / (fontSize * CHAR_WIDTH)));
  const text = name.length > characters ? `${name.slice(0, characters - 1)}…` : name;
  return { text, fontSize, overflows: text.length * fontSize * CHAR_WIDTH > markWidth };
}

export function squareMarketLabel(name, side, zoom) {
  const visibleSide = side * zoom;
  if (visibleSide < 19) return null;
  const fontSize = Math.min(13, Math.max(9, side * .27)) / Math.pow(zoom, .8);
  return fitMarketLabel(name, side - 4 / zoom, fontSize, { minCharacters: 10 });
}
